// Tabs. `Tabs` is an ARIA tablist (arrow keys, Home/End, roving tabindex) for in-page panels; `LinkTabs` is the routed
// variant (a nav of links with aria-current) used by the engagement workspace, where each tab is its own URL.
import { useRef, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

export interface TabDef { id: string; label: ReactNode; count?: number }

export function Tabs({ tabs, value, onChange, label, idPrefix = "tab" }: { tabs: TabDef[]; value: string; onChange: (id: string) => void; label: string; idPrefix?: string }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (i: number) => { const n = (i + tabs.length) % tabs.length; refs.current[n]?.focus(); onChange(tabs[n].id); };
  return (
    <div role="tablist" aria-label={label} className="relative flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((t, i) => {
        const on = t.id === value;
        return (
          <button key={t.id} ref={(el) => { refs.current[i] = el; }} role="tab" id={`${idPrefix}-${t.id}`} aria-selected={on} aria-controls={`${idPrefix}-panel-${t.id}`}
            tabIndex={on ? 0 : -1} onClick={() => onChange(t.id)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") { e.preventDefault(); move(i + 1); }
              else if (e.key === "ArrowLeft") { e.preventDefault(); move(i - 1); }
              else if (e.key === "Home") { e.preventDefault(); move(0); }
              else if (e.key === "End") { e.preventDefault(); move(tabs.length - 1); }
            }}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm ${on ? "border-brand font-semibold text-brand" : "border-transparent text-muted hover:text-ink"}`}>
            {t.label}{t.count !== undefined && <span className="ml-1.5 rounded bg-sunken px-1 text-xs tabular-nums text-muted">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ id, value, idPrefix = "tab", children }: { id: string; value: string; idPrefix?: string; children: ReactNode }) {
  if (id !== value) return null;
  return <div role="tabpanel" id={`${idPrefix}-panel-${id}`} aria-labelledby={`${idPrefix}-${id}`} tabIndex={0} className="pt-4 focus-visible:outline-none">{children}</div>;
}

export function LinkTabs({ tabs, label }: { tabs: Array<{ to: string; label: ReactNode; end?: boolean; count?: number }>; label: string }) {
  return (
    <nav aria-label={label} className="relative flex gap-1 overflow-x-auto border-b border-line">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end}
          className={({ isActive }) => `-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm ${isActive ? "border-brand font-semibold text-brand" : "border-transparent text-muted hover:text-ink"}`}>
          {t.label}{t.count !== undefined && <span className="ml-1.5 rounded bg-sunken px-1 text-xs tabular-nums text-muted">{t.count}</span>}
        </NavLink>
      ))}
    </nav>
  );
}
